// IMPORTANT: This file is the dashboard's mirror of
// backend/internal/publisher/presets.go. Every hex value here must match
// the corresponding Go preset exactly. When you change a value here,
// change presets.go in the same commit. A unit test
// (themePresets.test.ts) reads both files at test time and asserts
// equality.
import type { CSSProperties } from 'react';
import type { Theme, ThemePresetSlug } from '@/types/theme';

export const EDITORIAL_WARM: Theme = {
  palette: {
    background: '#fafaf9',
    surface: '#ffffff',
    surfaceAlt: '#f5f5f4',
    text: '#1c1917',
    textMuted: '#57534e',
    accent: '#0d9488',
    accentDark: '#0f766e',
    accentSoft: '#ccfbf1',
    warning: '#d97706',
    warningSoft: '#fef3c7',
    danger: '#b91c1c',
    dangerSoft: '#fee2e2',
    info: '#1d4ed8',
    infoSoft: '#dbeafe',
    border: '#e7e5e4',
    rule: '#e7e5e4',
  },
  typography: {
    serif: 'Newsreader',
    sans: 'Inter',
    mono: 'JetBrains Mono',
  },
};

export const CLINICAL_CLEAN: Theme = {
  palette: {
    background: '#f8fafc',
    surface: '#ffffff',
    surfaceAlt: '#f1f5f9',
    text: '#0f172a',
    textMuted: '#475569',
    accent: '#0369a1',
    accentDark: '#075985',
    accentSoft: '#e0f2fe',
    warning: '#b45309',
    warningSoft: '#fef3c7',
    danger: '#b91c1c',
    dangerSoft: '#fee2e2',
    info: '#1d4ed8',
    infoSoft: '#dbeafe',
    border: '#e2e8f0',
    rule: '#cbd5e1',
  },
  typography: {
    serif: 'Source Serif Pro',
    sans: 'Source Sans Pro',
    mono: 'JetBrains Mono',
  },
};

export const MODERN_MINIMAL: Theme = {
  palette: {
    background: '#ffffff',
    surface: '#ffffff',
    surfaceAlt: '#fafafa',
    text: '#171717',
    textMuted: '#737373',
    accent: '#2563eb',
    accentDark: '#1d4ed8',
    accentSoft: '#dbeafe',
    warning: '#ea580c',
    warningSoft: '#ffedd5',
    danger: '#dc2626',
    dangerSoft: '#fee2e2',
    info: '#0284c7',
    infoSoft: '#e0f2fe',
    border: '#e5e5e5',
    rule: '#f5f5f5',
  },
  typography: {
    serif: 'Inter',
    sans: 'Inter',
    mono: 'JetBrains Mono',
  },
};

export const BOLD_CONTRAST: Theme = {
  palette: {
    background: '#fafafa',
    surface: '#ffffff',
    surfaceAlt: '#f5f5f5',
    text: '#0a0a0a',
    textMuted: '#525252',
    accent: '#1f2937',
    accentDark: '#030712',
    accentSoft: '#e5e7eb',
    warning: '#92400e',
    warningSoft: '#fef3c7',
    danger: '#991b1b',
    dangerSoft: '#fee2e2',
    info: '#1e3a8a',
    infoSoft: '#dbeafe',
    border: '#d4d4d4',
    rule: '#e5e5e5',
  },
  typography: {
    serif: 'Playfair Display',
    sans: 'DM Sans',
    mono: 'JetBrains Mono',
  },
};

export interface ThemePresetMeta {
  slug: ThemePresetSlug;
  name: string;
  description: string;
  theme: Theme;
}

export function detectPreset(theme: Theme): ThemePresetSlug | null {
  for (const p of THEME_PRESETS) {
    if (JSON.stringify(p.theme) === JSON.stringify(theme)) return p.slug;
  }
  return null;
}

// renderThemeVars produces an inline-style object that sets every
// --theme-* CSS custom property from the supplied Theme. Wrap a DOM tree in
// a <div data-theme-preview style={renderThemeVars(theme)}> and every
// descendant Tailwind utility that reads var(--theme-*) resolves to the
// theme's colours and fonts.
export function renderThemeVars(theme: Theme): CSSProperties {
  return {
    '--theme-bg': theme.palette.background,
    '--theme-surface': theme.palette.surface,
    '--theme-surface-alt': theme.palette.surfaceAlt,
    '--theme-ink': theme.palette.text,
    '--theme-muted': theme.palette.textMuted,
    '--theme-accent': theme.palette.accent,
    '--theme-accent-dark': theme.palette.accentDark,
    '--theme-accent-soft': theme.palette.accentSoft,
    '--theme-warning': theme.palette.warning,
    '--theme-warning-soft': theme.palette.warningSoft,
    '--theme-danger': theme.palette.danger,
    '--theme-danger-soft': theme.palette.dangerSoft,
    '--theme-info': theme.palette.info,
    '--theme-info-soft': theme.palette.infoSoft,
    '--theme-border': theme.palette.border,
    '--theme-rule': theme.palette.rule,
    '--theme-font-serif': `'${theme.typography.serif}', Georgia, serif`,
    '--theme-font-sans': `'${theme.typography.sans}', system-ui, sans-serif`,
    '--theme-font-mono': `'${theme.typography.mono}', ui-monospace, monospace`,
  } as CSSProperties;
}

export const THEME_PRESETS: ThemePresetMeta[] = [
  {
    slug: 'editorial-warm',
    name: 'Editorial Warm',
    description:
      'Newsreader serif headings, teal accent, warm off-white. A warm editorial default.',
    theme: EDITORIAL_WARM,
  },
  {
    slug: 'clinical-clean',
    name: 'Clinical Clean',
    description:
      'Source Sans body, blue-gray palette, cool background. A clean, clinical aesthetic.',
    theme: CLINICAL_CLEAN,
  },
  {
    slug: 'modern-minimal',
    name: 'Modern Minimal',
    description:
      'Inter throughout, near-monochrome, single bright accent. Generous whitespace.',
    theme: MODERN_MINIMAL,
  },
  {
    slug: 'bold-contrast',
    name: 'Bold Contrast',
    description:
      'Playfair Display headings, DM Sans body, near-black on near-white. Magazine feel.',
    theme: BOLD_CONTRAST,
  },
];
